// WinterPromoBanner — slim seasonal strip that renders at the top of
// every public page from Dec 1 → Feb 28/29 (auto-swaps back to normal
// in March). Mirrors FallPromoBanner's structure so the seasonal
// rhythm feels consistent across the site.
//
// Oct 7 2026 · Karine ask (option b continued) — "winter palette:
// icy teal / silver / pine green". Palette captured in WINTER export
// so future winter surfaces (newsletter, OG images, print flyers)
// pull from the same source of truth.
//
// Dismissible for 24h via localStorage. Hidden on staff/admin/kiosk.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Snowflake } from "lucide-react";

export const WINTER = {
  icyTeal:    "#9EC9CB",   // frosted spa teal
  deepTeal:   "#2E5F6B",   // headlines
  silver:     "#C9CFD4",   // satin accent
  silverSoft: "#E6ECEF",   // card bg
  pineGreen:  "#2D4A3E",   // CTA + serif italic
  pineDark:   "#1F3B30",
  slateBlue:  "#324B5C",   // overlays
  snowCream:  "#F0F3F5",   // page bg
};

// Dec 1 → end of Feb (handles leap year via Feb 29 / 28 automatically
// since JS Date rolls over).
function isWinterSeason(now = new Date()) {
  const month = now.getMonth(); // 0-based
  // December (11), January (0), or February (1)
  return month === 11 || month === 0 || month === 1;
}

const DISMISS_KEY = "kl_winter_banner_dismissed_until";

const SUPPRESSED_PREFIXES = [
  "/staff", "/admin", "/md", "/consultation/", "/consent",
  "/welcome", "/checkin", "/signage", "/lobby", "/kiosk",
];

export default function WinterPromoBanner() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && Date.now() < until) setDismissed(true);
    } catch {}
  }, []);

  const suppressed = useMemo(
    () => SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p)),
    [location.pathname]
  );

  const visible = isWinterSeason() && !suppressed && !dismissed;

  // Oct 7 2026 fix — see FallPromoBanner comment. Banner is fixed at
  // the top with z-[200] (above Navbar z-100 and TopBar z-50) and
  // exposes its height via `--season-banner-h` so the fixed TopBar
  // wrapper and Navbar can shift down.
  useEffect(() => {
    const root = document.documentElement;
    if (visible) {
      root.style.setProperty("--season-banner-h", "36px");
    } else {
      root.style.removeProperty("--season-banner-h");
    }
    return () => root.style.removeProperty("--season-banner-h");
  }, [visible]);

  if (!visible) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch {}
    setDismissed(true);
  };

  // Default winter CTA — points to /promotions until Karine wires a
  // dedicated winter hero promo (e.g. "Frost Reset Facial", "Snow Glow
  // Package"). To switch the deep-link later, just edit the `to=` on
  // the <Link> and the two spans below.
  return (
    <div
      className="fixed top-0 left-0 right-0 w-full z-[200]"
      style={{
        background: `linear-gradient(90deg, ${WINTER.deepTeal} 0%, ${WINTER.slateBlue} 50%, ${WINTER.deepTeal} 100%)`,
        borderBottom: `1px solid ${WINTER.icyTeal}44`,
      }}
      data-testid="winter-promo-banner"
    >
      <Link
        to="/promotions"
        className="flex items-center justify-center gap-2 sm:gap-3 pl-3 pr-10 py-2 sm:py-3 text-center text-[12px] sm:text-[13px] font-medium tracking-wide"
        style={{ color: WINTER.snowCream }}
      >
        {/* Snowflake — always visible on mobile too so the winter
            signal reads instantly even when the "Winter at Karine
            Lauzon" prefix collapses. */}
        <Snowflake size={16} className="flex-shrink-0" style={{ color: WINTER.icyTeal }} />
        <span className="font-serif italic hidden md:inline" style={{ color: WINTER.icyTeal }}>
          Winter at Karine Lauzon
        </span>
        <span className="hidden md:inline" style={{ opacity: 0.4 }}>·</span>

        {/* Mobile copy — short, no wrap */}
        <span className="sm:hidden whitespace-nowrap">
          <strong style={{ color: WINTER.snowCream }}>Winter Reset</strong>
          <span className="mx-1" style={{ opacity: 0.5 }}>·</span>
          <span style={{ color: WINTER.silver }}>Promos</span>
        </span>

        {/* Tablet/desktop copy — full */}
        <span className="hidden sm:inline">
          <strong style={{ color: WINTER.snowCream }}>Winter Reset</strong>
          <span className="mx-1.5" style={{ opacity: 0.5 }}>·</span>
          Hydration & brightening packages
          <span className="hidden md:inline mx-1.5" style={{ color: WINTER.silver, opacity: 0.75 }}>
            · See all promos
          </span>
        </span>

        <span
          className="ml-1 sm:ml-2 inline-flex items-center gap-1 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wider transition-transform hover:scale-105 whitespace-nowrap"
          style={{
            background: WINTER.icyTeal,
            color: WINTER.pineDark,
            boxShadow: `0 2px 8px ${WINTER.pineGreen}66`,
          }}
          data-testid="winter-promo-banner-cta"
        >
          Explore →
        </span>
      </Link>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss winter promo banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/10 transition"
        style={{ color: WINTER.snowCream }}
        data-testid="winter-promo-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
